import colors from '../variables/colors'
import React from 'react'
import PropTypes from 'prop-types'
import styles from './avatar.module.scss'
import clsx from 'clsx'
import Typography from '../Typography'
import Icon from '../Icon'

const Avatar = ({
  color,
  haloColor = null,
  src = null,
  children,
  width,
  size = 'm',
  title,
  flexJustify = 'center',
  bgColor = '',
  className,
  additionalIcon = null,
  coverComponent = null,
  ...rest
}) => {
  const widthVal = width ? width : null
  const heightVal = width ? width : null
  const borderColor = haloColor ? `var(--color-${haloColor})` : null

  const Wrapper = (props) => (
    <div
      {...props}
      className={clsx(
        styles.root,
        {
          [styles[size]]: !haloColor,
          [styles[`${size}-withHalo`]]: haloColor,
          [styles.withHalo]: haloColor,
          [styles[`flex-justify-${flexJustify}`]]: flexJustify,
        },
        className,
      )}
      title={title}
      style={{
        ...props.style,
        width: widthVal,
        height: heightVal,
        borderColor: borderColor,
      }}
    >
      <div
        className={clsx(
          styles['avatar-wrapper'],
          styles[`flex-justify-${flexJustify}`],
          {
            [`bg-color-static-${bgColor}`]: bgColor && !src,
          },
        )}
      >
        {props.children}
        {!!coverComponent &&
          React.cloneElement(coverComponent, {
            ...coverComponent.props,
            className: clsx(
              coverComponent.props.className,
              styles['cover-component'],
            ),
          })}
      </div>
      {additionalIcon &&
        React.createElement(
          'div',
          {
            width: additionalIcon.props.width || '16px',
            className: clsx(
              additionalIcon.props.className,
              styles['additional-icon'],
            ),
          },
          additionalIcon,
        )}
    </div>
  )

  if (children) {
    return (
      <Wrapper {...rest}>
        {React.cloneElement(children, { width: widthVal, height: heightVal })}
      </Wrapper>
    )
  }

  if (src) {
    return (
      <Wrapper {...rest}>
        <img src={src} className={styles.image} alt={''} />
      </Wrapper>
    )
  }

  if (title && title.trim().length > 0) {
    const abbr = title
      .trim()
      .replace(/['!"#$%&\\()*+,\-./:;<=>?@[\]^`{|}~]/g, '')
      .toLowerCase()
      .split(' ')
      .splice(0, 3)
      .reduce((acc, word) => {
        acc = acc + (word[0] || '')
        return acc
      }, '')

    return (
      <Wrapper {...rest}>
        <Typography variant={'p8'} color={color || 'Label-Onsurface'} noMargin>
          {abbr}
        </Typography>
      </Wrapper>
    )
  }

  return (
    <Wrapper {...rest}>
      <Icon name={'logo'} color={color || 'Interface-Onsurface'} size={'m'} />
    </Wrapper>
  )
}

Avatar.propTypes = {
  style: PropTypes.string,
  children: PropTypes.node,
  color: PropTypes.string,
  haloColor: PropTypes.oneOf(colors),
  src: PropTypes.string,
  size: PropTypes.oneOf(['s', 'm', 'l']),
  title: PropTypes.string,
  className: PropTypes.string,
  bgColor: PropTypes.oneOf(colors),
  width: PropTypes.string,
  coverComponent: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  additionalIcon: PropTypes.oneOfType([null, PropTypes.object]),
  flexJustify: PropTypes.oneOf([
    'flex-start',
    'center',
    'flex-end',
    'space-between',
    'space-around',
    'space-evenly',
  ]),
}

Avatar.defaultProps = {
  size: 'l',
}

export default Avatar
